/**
 * What comes before the app: choosing a program.
 *
 * BEST hosts several, independent of one another, and every screen is about
 * exactly one. So nothing renders until one is resolved - which is normally
 * instant, because the server remembers where each person left off and answers
 * with it on the first request.
 *
 * The state this exists for is the other one: an install with nothing
 * registered yet. That is not an error and must not look like one, so it gets
 * a page that says what to do rather than a failed request.
 */

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CreateProjectDialog from './CreateProjectDialog'
import { signInUrl } from '../../api'
import { installRole, usePrincipal, useRefreshPrincipal } from '../../state/AppData'
import { useProject } from '../../state/Project'

function Centerd({ children }: { children: ReactNode }) {
  return (
    <Container maxWidth="sm" sx={{ py: 10 }}>
      <Stack spacing={3}>{children}</Stack>
    </Container>
  )
}

export default function ProjectGate({ children }: { children: ReactNode }) {
  const principal = usePrincipal()
  const refreshPrincipal = useRefreshPrincipal()
  const { projects, current, singleDatabase, loading, choose } = useProject()
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState('')

  /*
   * What a person may do is their role in THIS program's GitLab project, so the
   * role shown in the bar and every control that depends on it must follow the
   * program. whoami is read once at start-up, before a program is resolved;
   * this re-reads it whenever the program in view changes.
   *
   * Keyed on the slug alone, so a server that never reports a program (single
   * database, anonymous) is asked once and not in a loop.
   */
  const slug = current?.slug
  useEffect(() => {
    if (slug && !principal.anonymous && principal.program !== undefined
        && principal.program !== slug) {
      void refreshPrincipal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  // One model, named by BEST_DATABASE_URL at start-up, with no registry entry
  // describing it. There is nothing to choose between.
  if (singleDatabase || current) return <>{children}</>

  if (projects.length === 0) {
    return (
      <Centerd>
        <Typography variant="h5">No programs available</Typography>
        <Typography color="text.secondary">
          Either no program has been registered yet, or none of the registered
          programs is one your GitLab account can see: BEST shows a program only
          to members of its GitLab project. If you expected one, ask a maintainer
          of that project to add you.
        </Typography>
        {installRole(principal) === 'admin' ? (
          <Box>
            <Button variant="contained" onClick={() => setCreating(true)}>
              Register a program
            </Button>
          </Box>
        ) : (
          <Typography color="text.secondary">
            {principal.anonymous
              ? 'Sign in to register one.'
              : `Registering a program needs admin in BEST; ${
                  installRole(principal)
                    ? `your GitLab access to BEST is ${installRole(principal)}`
                    : 'you are not a member of the BEST project'
                }.`}
          </Typography>
        )}
        {principal.anonymous ? (
          <Box>
            <Button variant="contained" href={signInUrl()}>
              Sign in with GitLab
            </Button>
          </Box>
        ) : null}
        <Divider />
        <Typography variant="body2" color="text.secondary">
          From a terminal, an existing database can be taken over instead:
          <Box component="code" sx={{ display: 'block', mt: 1 }}>
            best project adopt balrog --from best.db
          </Box>
        </Typography>
        <CreateProjectDialog open={creating} onClose={() => setCreating(false)} />
      </Centerd>
    )
  }

  // Programs exist but none is selected - a first sign-in, or the last one
  // was archived or hidden.
  return (
    <Centerd>
      <Typography variant="h5">Choose a program</Typography>
      <Typography color="text.secondary">
        Your choice is remembered, so this only appears once.
      </Typography>
      <Stack spacing={1}>
        {projects.map((project) => (
          <Card key={project.slug} variant="outlined">
            <CardActionArea
              disabled={Boolean(busy)}
              onClick={() => {
                setBusy(project.slug)
                void choose(project.slug).finally(() => setBusy(''))
              }}
              sx={{ p: 2 }}
            >
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <AccountTreeIcon color="primary" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{project.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {project.description ||
                      `${project.file_count} file${project.file_count === 1 ? '' : 's'} · ${project.default_branch}`}
                  </Typography>
                </Box>
              </Stack>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
      {installRole(principal) === 'admin' ? (
        <Box>
          <Button onClick={() => setCreating(true)}>New program…</Button>
        </Box>
      ) : null}
      <CreateProjectDialog open={creating} onClose={() => setCreating(false)} />
    </Centerd>
  )
}
